<template>
  <div class="utilidad-reporte">

    <!-- Encabezado -->
    <div class="utilidad-reporte__cabecera d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <h5 class="utilidad-titulo mb-0">
        <i class="bi bi-graph-up-arrow me-2"></i>Utilidad Aproximada
      </h5>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <span v-if="reporte" class="utilidad-periodo-badge">
          {{ reporte.periodo.etiqueta }}
        </span>
        <template v-if="reporte && !cargando">
          <button
            class="btn btn-sm btn-outline-success"
            @click="exportarXlsx"
            :disabled="exportandoXlsx"
            title="Descargar como Excel"
          >
            <span v-if="exportandoXlsx" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-file-earmark-excel me-1"></i>Excel
          </button>
          <button
            class="btn btn-sm btn-outline-danger"
            @click="exportarPdf"
            :disabled="exportandoPdf"
            title="Descargar como PDF"
          >
            <span v-if="exportandoPdf" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-file-earmark-pdf me-1"></i>PDF
          </button>
        </template>
      </div>
    </div>

    <!-- Panel filtros -->
    <div class="utilidad-panel mb-3">
      <div class="utilidad-panel__cuerpo">

        <!-- Toggle modo -->
        <div class="d-flex gap-2 mb-3">
          <button
            :class="['btn btn-sm', modoFiltro === 'anio' ? 'btn-primary' : 'btn-outline-secondary']"
            @click="modoFiltro = 'anio'"
          >
            <i class="bi bi-calendar2-range me-1"></i>Por año
          </button>
          <button
            :class="['btn btn-sm', modoFiltro === 'rango' ? 'btn-primary' : 'btn-outline-secondary']"
            @click="modoFiltro = 'rango'"
          >
            <i class="bi bi-calendar3 me-1"></i>Rango de fechas
          </button>
        </div>

        <!-- Selector por año -->
        <div v-if="modoFiltro === 'anio'" class="d-flex flex-wrap gap-2 align-items-center">
          <span class="utilidad-label-muted me-1">Seleccionar año:</span>
          <button
            v-for="a in aniosDisponibles"
            :key="a"
            :class="['btn btn-sm', anioSeleccionado === a ? 'btn-primary' : 'btn-outline-secondary']"
            @click="seleccionarAnio(a)"
            :disabled="cargando"
          >{{ a }}</button>
        </div>

        <!-- Rango personalizado -->
        <div v-if="modoFiltro === 'rango'" class="row g-2 align-items-end">
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Fecha desde</label>
            <input type="date" class="form-control form-control-sm utilidad-input" v-model="filtros.fecha_desde" :max="filtros.fecha_hasta" />
          </div>
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Fecha hasta</label>
            <input type="date" class="form-control form-control-sm utilidad-input" v-model="filtros.fecha_hasta" :min="filtros.fecha_desde" />
          </div>
          <div class="col-auto d-flex gap-1">
            <button class="btn btn-sm btn-primary" @click="generarReporte" :disabled="cargando">
              <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-search me-1"></i>
              {{ cargando ? 'Generando…' : 'Generar' }}
            </button>
            <button class="btn btn-sm btn-link" style="color:var(--text-muted)" @click="limpiarRango" :disabled="cargando">
              Limpiar
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMensaje" class="utilidad-alerta utilidad-alerta--error d-flex align-items-center gap-2 mb-3">
      <i class="bi bi-x-circle-fill"></i>
      <span>{{ errorMensaje }}</span>
    </div>

    <!-- Cargando -->
    <div v-if="cargando" class="utilidad-estado-vacio">
      <span class="spinner-border spinner-border-sm me-2"></span>Generando reporte…
    </div>

    <template v-if="reporte && !cargando">

      <!-- KPI cards -->
      <div class="row g-3 mb-3">
        <div class="col-sm-6 col-xl-3">
          <div class="utilidad-kpi">
            <div class="utilidad-kpi__valor" style="color:var(--primary-color)">
              {{ formatearMoneda(reporte.resumen.total_ventas_sin_iva) }}
            </div>
            <div class="utilidad-kpi__etiqueta">Ventas (sin IVA)</div>
            <div class="utilidad-kpi__sub">{{ reporte.resumen.num_facturas }} factura(s)</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl-3">
          <div class="utilidad-kpi">
            <div class="utilidad-kpi__valor" style="color:var(--accent-danger)">
              {{ formatearMoneda(reporte.resumen.total_compras_sin_iva) }}
            </div>
            <div class="utilidad-kpi__etiqueta">Compras (sin IVA)</div>
            <div class="utilidad-kpi__sub">{{ reporte.resumen.num_compras }} compra(s)</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl-3">
          <div class="utilidad-kpi">
            <div
              class="utilidad-kpi__valor"
              :style="{ color: reporte.resumen.utilidad >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' }"
            >
              {{ formatearMoneda(reporte.resumen.utilidad) }}
            </div>
            <div class="utilidad-kpi__etiqueta">Utilidad del período</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl-3">
          <div class="utilidad-kpi">
            <div
              class="utilidad-kpi__valor"
              :style="{ color: reporte.resumen.margen_porcentaje >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' }"
            >
              {{ reporte.resumen.margen_porcentaje }}%
            </div>
            <div class="utilidad-kpi__etiqueta">Margen de utilidad</div>
          </div>
        </div>
      </div>

      <!-- Resumen por año -->
      <div v-if="reporte.resumen_anual.length > 0" class="utilidad-panel mb-3">
        <div class="utilidad-panel__cabecera d-flex align-items-center justify-content-between">
          <span><i class="bi bi-bar-chart-line me-2"></i>Resumen por año</span>
        </div>
        <div class="table-responsive">
          <table class="utilidad-tabla">
            <thead>
              <tr>
                <th>Año</th>
                <th class="text-end">Ventas (sin IVA)</th>
                <th class="text-end">Ventas (con IVA)</th>
                <th class="text-end">Compras (sin IVA)</th>
                <th class="text-end">Utilidad</th>
                <th class="text-end">Margen</th>
                <th class="text-end">Facturas</th>
                <th class="text-end">Compras</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="fila in reporte.resumen_anual"
                :key="fila.anio"
                class="utilidad-fila"
                :class="{ 'utilidad-fila--activa': esFilaActiva(fila.anio) }"
              >
                <td>
                  <span class="fw-semibold">{{ fila.anio }}</span>
                  <span v-if="esFilaActiva(fila.anio)" class="utilidad-badge-sel ms-1">seleccionado</span>
                </td>
                <td class="text-end">{{ formatearMoneda(fila.total_ventas_sin_iva) }}</td>
                <td class="text-end utilidad-muted">{{ formatearMoneda(fila.total_ventas_con_iva) }}</td>
                <td class="text-end" style="color:var(--accent-danger)">{{ formatearMoneda(fila.total_compras_sin_iva) }}</td>
                <td
                  class="text-end fw-semibold"
                  :style="{ color: fila.utilidad >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' }"
                >{{ formatearMoneda(fila.utilidad) }}</td>
                <td
                  class="text-end"
                  :style="{ color: fila.margen_porcentaje >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' }"
                >{{ fila.margen_porcentaje }}%</td>
                <td class="text-end">{{ fila.num_facturas }}</td>
                <td class="text-end">{{ fila.num_compras }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Facturas de venta -->
      <div class="utilidad-panel mb-3">
        <div class="utilidad-panel__cabecera d-flex align-items-center justify-content-between">
          <span><i class="bi bi-receipt me-2"></i>Facturas de venta &mdash; {{ reporte.periodo.etiqueta }}</span>
          <span class="utilidad-conteo" style="background:var(--primary-color)">{{ reporte.facturas.length }} doc.</span>
        </div>
        <div v-if="reporte.facturas.length > 0" class="table-responsive">
          <table class="utilidad-tabla">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Cliente</th>
                <th class="text-end">Subtotal</th>
                <th class="text-end">IVA</th>
                <th class="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="f in reporte.facturas" :key="f.id" class="utilidad-fila">
                <td class="text-nowrap">{{ f.fecha_emision }}</td>
                <td>{{ f.cliente }}</td>
                <td class="text-end">{{ formatearMoneda(f.subtotal) }}</td>
                <td class="text-end utilidad-muted">{{ formatearMoneda(f.iva) }}</td>
                <td class="text-end fw-semibold">{{ formatearMoneda(f.total) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="utilidad-pie">
                <td colspan="2">Total</td>
                <td class="text-end">{{ formatearMoneda(reporte.resumen.total_ventas_sin_iva) }}</td>
                <td></td>
                <td class="text-end">{{ formatearMoneda(reporte.resumen.total_ventas_con_iva) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="utilidad-estado-vacio">
          <i class="bi bi-receipt fs-3 d-block mb-2"></i>No hay facturas de venta autorizadas en este período.
        </div>
      </div>

      <!-- Compras -->
      <div class="utilidad-panel mb-3">
        <div class="utilidad-panel__cabecera d-flex align-items-center justify-content-between">
          <span><i class="bi bi-cart me-2"></i>Compras &mdash; {{ reporte.periodo.etiqueta }}</span>
          <span class="utilidad-conteo" style="background:var(--accent-danger)">{{ reporte.compras.length }} doc.</span>
        </div>
        <div v-if="reporte.compras.length > 0" class="table-responsive">
          <table class="utilidad-tabla">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Proveedor</th>
                <th>N.° Comprobante</th>
                <th class="text-end">Subtotal</th>
                <th class="text-end">IVA</th>
                <th class="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in reporte.compras" :key="c.id" class="utilidad-fila">
                <td class="text-nowrap">{{ c.fecha_emision }}</td>
                <td>{{ c.proveedor }}</td>
                <td>{{ c.numero_factura }}</td>
                <td class="text-end" style="color:var(--accent-danger)">{{ formatearMoneda(c.subtotal) }}</td>
                <td class="text-end utilidad-muted">{{ formatearMoneda(c.iva) }}</td>
                <td class="text-end fw-semibold" style="color:var(--accent-danger)">{{ formatearMoneda(c.total) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="utilidad-pie">
                <td colspan="3">Total</td>
                <td class="text-end" style="color:var(--accent-danger)">{{ formatearMoneda(reporte.resumen.total_compras_sin_iva) }}</td>
                <td></td>
                <td class="text-end" style="color:var(--accent-danger)">
                  {{ formatearMoneda(reporte.compras.reduce((s, c) => s + parseFloat(c.total || 0), 0)) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="utilidad-estado-vacio">
          <i class="bi bi-cart fs-3 d-block mb-2"></i>No hay compras registradas en este período.
        </div>
      </div>

    </template>

    <!-- Estado inicial -->
    <div v-if="!reporte && !cargando && !errorMensaje" class="utilidad-panel">
      <div class="utilidad-estado-vacio">
        <i class="bi bi-graph-up-arrow fs-2 d-block mb-2"></i>
        Selecciona un año o rango de fechas para generar el reporte.
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'
import themeManager from '@/store/theme'
import * as XLSX from 'xlsx'
import { fechaIsoSistema } from '@/utils/fechaSistema'

export default {
  name: 'ClientReportesUtilidadAproximada',

  data() {
    const hoy = new Date()
    return {
      modoFiltro: 'anio',
      anioSeleccionado: hoy.getFullYear(),
      filtros: {
        fecha_desde: `${hoy.getFullYear()}-01-01`,
        fecha_hasta: fechaIsoSistema(hoy),
      },
      cargando: false,
      errorMensaje: null,
      reporte: null,
      exportandoXlsx: false,
      exportandoPdf: false,
    }
  },

  mounted() {
    this.generarReporte()
  },

  computed: {
    themeClass() {
      return themeManager.themeClass
    },

    aniosDisponibles() {
      const actual = new Date().getFullYear()
      const set = new Set([actual])
      if (this.reporte?.resumen_anual) {
        this.reporte.resumen_anual.forEach(r => set.add(Number(r.anio)))
      }
      return Array.from(set).sort((a, b) => b - a)
    },
  },

  methods: {
    async generarReporte() {
      this.cargando = true
      this.errorMensaje = null
      this.reporte = null
      try {
        const params = new URLSearchParams()
        if (this.modoFiltro === 'anio') {
          params.set('anio', this.anioSeleccionado)
        } else {
          if (this.filtros.fecha_desde) params.set('fecha_desde', this.filtros.fecha_desde)
          if (this.filtros.fecha_hasta) params.set('fecha_hasta', this.filtros.fecha_hasta)
        }
        const { data } = await api.get(`/reportes/utilidad-aproximada?${params}`)
        if (data.success) {
          this.reporte = data
        } else {
          this.errorMensaje = data.error || 'No se pudieron obtener los datos.'
        }
      } catch (err) {
        this.errorMensaje = err?.response?.data?.error || 'Error al conectar con el servidor.'
      } finally {
        this.cargando = false
      }
    },

    seleccionarAnio(anio) {
      this.anioSeleccionado = anio
      this.generarReporte()
    },

    limpiarRango() {
      const hoy = new Date()
      this.filtros.fecha_desde = `${hoy.getFullYear()}-01-01`
      this.filtros.fecha_hasta = fechaIsoSistema(hoy)
      this.reporte = null
      this.errorMensaje = null
    },

    formatearMoneda(valor) {
      const num = parseFloat(valor) || 0
      return `$${num.toFixed(2)}`
    },

    esFilaActiva(anio) {
      return this.modoFiltro === 'anio' && Number(anio) === this.anioSeleccionado
    },

    // ── Exportar Excel ──────────────────────────────────────────────────
    exportarXlsx() {
      if (!this.reporte) return
      this.exportandoXlsx = true
      try {
        const r = this.reporte
        const wb = XLSX.utils.book_new()

        // Hoja 1: Resumen
        const resumen = [
          ['Reporte de Utilidad Aproximada'],
          ['Período', r.periodo.etiqueta],
          [],
          ['Concepto', 'Valor'],
          ['Ventas sin IVA',   r.resumen.total_ventas_sin_iva],
          ['Ventas con IVA',   r.resumen.total_ventas_con_iva],
          ['Compras sin IVA',  r.resumen.total_compras_sin_iva],
          ['Utilidad',         r.resumen.utilidad],
          ['Margen (%)',        r.resumen.margen_porcentaje],
          ['N.° Facturas',     r.resumen.num_facturas],
          ['N.° Compras',      r.resumen.num_compras],
        ]
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumen), 'Resumen')

        // Hoja 2: Facturas de venta
        const cabF = ['Fecha', 'Cliente', 'Subtotal', 'IVA', 'Total']
        const filasF = r.facturas.map(f => [
          f.fecha_emision,
          f.cliente,
          parseFloat(f.subtotal),
          parseFloat(f.iva),
          parseFloat(f.total),
        ])
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.aoa_to_sheet([cabF, ...filasF]),
          'Facturas venta'
        )

        // Hoja 3: Compras
        const cabC = ['Fecha', 'Proveedor', 'N.° Comprobante', 'Subtotal', 'IVA', 'Total']
        const filasC = r.compras.map(c => [
          c.fecha_emision,
          c.proveedor,
          c.numero_factura,
          parseFloat(c.subtotal),
          parseFloat(c.iva),
          parseFloat(c.total),
        ])
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.aoa_to_sheet([cabC, ...filasC]),
          'Compras'
        )

        const etiq = r.periodo.etiqueta.replace(/[^a-z0-9]/gi, '_')
        XLSX.writeFile(wb, `utilidad_aproximada_${etiq}.xlsx`)
      } finally {
        this.exportandoXlsx = false
      }
    },

    // ── Exportar PDF ────────────────────────────────────────────────────
    async exportarPdf() {
      if (!this.reporte) return
      this.exportandoPdf = true
      try {
        const params = new URLSearchParams()
        if (this.modoFiltro === 'anio') {
          params.set('anio', this.anioSeleccionado)
        } else {
          if (this.filtros.fecha_desde) params.set('fecha_desde', this.filtros.fecha_desde)
          if (this.filtros.fecha_hasta) params.set('fecha_hasta', this.filtros.fecha_hasta)
        }
        const response = await api.get(
          `/reportes/utilidad-aproximada/pdf?${params}`,
          { responseType: 'blob' }
        )
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url  = URL.createObjectURL(blob)
        const a    = document.createElement('a')
        const etiq = this.reporte.periodo.etiqueta.replace(/[^a-z0-9]/gi, '_')
        a.href     = url
        a.download = `utilidad_aproximada_${etiq}.pdf`
        a.click()
        URL.revokeObjectURL(url)
      } catch {
        this.errorMensaje = 'No se pudo generar el PDF. Intente nuevamente.'
      } finally {
        this.exportandoPdf = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Contenedor ── */
.utilidad-reporte {
  padding: 1.25rem;
}

.utilidad-titulo {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--primary-color);
}

.utilidad-periodo-badge {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--secondary-bg-soft);
  border: 1px solid var(--border-main);
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
}

/* ── Panel / Tarjeta ── */
.utilidad-panel {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.utilidad-panel__cuerpo {
  padding: 0.85rem 1rem;
}

.utilidad-panel__cabecera {
  padding: 0.6rem 0.85rem;
  background: var(--secondary-bg-soft);
  border-bottom: 1px solid var(--border-main);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
}

/* ── KPI cards ── */
.utilidad-kpi {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  text-align: center;
  padding: 1rem;
}

.utilidad-kpi__valor {
  font-size: 1.5rem;
  font-weight: 700;
}

.utilidad-kpi__etiqueta {
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
}

.utilidad-kpi__sub {
  font-size: 0.75rem;
  color: var(--text-muted);
}

/* ── Alertas ── */
.utilidad-alerta {
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  font-size: 0.875rem;
  border: 1px solid;
}

.utilidad-alerta--error {
  background: var(--accent-danger-light);
  border-color: var(--accent-danger);
  color: var(--accent-danger);
}

/* ── Estado vacío ── */
.utilidad-estado-vacio {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  font-size: 0.9rem;
}

/* ── Tabla ── */
.utilidad-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.utilidad-tabla thead tr {
  background: var(--secondary-bg-soft);
  border-bottom: 2px solid var(--border-main);
}

.utilidad-tabla thead th {
  padding: 0.6rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.utilidad-fila {
  border-bottom: 1px solid var(--border-main);
  transition: background 0.1s;
}

.utilidad-fila td {
  padding: 0.55rem 0.75rem;
  vertical-align: middle;
  color: var(--text-primary);
}

.utilidad-fila:hover {
  background: var(--table-hover);
}

.utilidad-fila--activa {
  background: var(--accent-info-light) !important;
  border-left: 3px solid var(--primary-color);
}

/* ── Pie de tabla ── */
.utilidad-pie td {
  padding: 0.55rem 0.75rem;
  font-weight: 600;
  color: var(--text-primary);
  background: var(--secondary-bg-soft);
  border-top: 2px solid var(--border-main);
}

/* ── Auxiliares ── */
.utilidad-muted {
  color: var(--text-muted);
  font-size: 0.82rem;
}

.utilidad-label-muted {
  font-size: 0.82rem;
  color: var(--text-muted);
}

.utilidad-conteo {
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
  border-radius: 20px;
  padding: 0.2rem 0.6rem;
}

.utilidad-badge-sel {
  font-size: 0.7rem;
  font-weight: 600;
  color: #fff;
  background: var(--primary-color);
  border-radius: 10px;
  padding: 0.1rem 0.45rem;
}

/* ── Input ── */
.utilidad-input {
  background: var(--input-bg);
  color: var(--text-primary);
  border-color: var(--border-main);
}

.utilidad-input:focus {
  border-color: var(--border-focus);
  box-shadow: none;
}
</style>
